import * as colors from "../constants/colors";
import * as breakpoints from "../breakpoints";
import * as languages from "../constants/languages";
import { css, StyleSheet } from "aphrodite";
import { english, spanish } from "../constants/strings";
import { handleUnknownError } from "../lib/errorHandler";
import { updateEmail } from "../api/account";
import { NotificationManager } from "react-notifications";
import Modal from "react-modal";
import PropTypes from "prop-types";
import React, { PureComponent } from "react";
import RoundButton from "./RoundButton";
import { rootUrl } from "../config.js";

const styles = StyleSheet.create({
	button: {
		width: 154,
	},
	buttonsContainer: {
		marginTop: 32,
		[breakpoints.mobileOnly]: {
			display: "flex",
			alignItems: "center",
			justifyContent: "center",
		},
	},
	cancelButton: {
		marginLeft: 16,
	},
	closeIconButton: {
		border: "none",
		background: "none",
		position: "absolute",
		padding: 0,
		top: 8,
		right: 8,
		[breakpoints.mobileOnly]: {
			padding: 4,
		},
	},
	header: {
		backgroundColor: colors.teal,
		paddingTop: 18,
		paddingBottom: 4,
		position: "relative",
	},
	currentEmailAddress: {
		lineHeight: 1.33,
		color: colors.black4,
		fontSize: 15,
		fontFamily:
			"TrebuchetMS, Trebuchet MS, Lucida Grande, Lucida Sans Unicode, Lucida Sans, Tahoma,sans-serif",
		marginTop: 20,
	},
	errorMessage: {
		color: colors.errorRed,
		fontSize: 12,
		lineHeight: 1.67,
		fontFamily:
			"TrebuchetMS, Trebuchet MS, Lucida Grande, Lucida Sans Unicode, Lucida Sans, Tahoma,sans-serif",
		marginTop: 6,
	},
	formItem: {
		marginTop: 22,
		display: "flex",
		flexDirection: "column",
	},
	fullScreenCover: {
		position: "fixed",
		height: "100vh",
		width: "100vw",
		backgroundColor: colors.transparentTeal,
		top: 0,
		left: 0,
		display: "flex",
		justifyContent: "center",
		alignItems: "center",
		zIndex: 1,

		"@media (max-height: 750px)": {
			alignItems: "flex-start",
			overflow: "auto",
		},
	},
	headerText: {
		fontSize: 60,
		fontFamily: "PopulaireLight",
		fontWeight: 300,
		lineHeight: 0.92,
		letterSpacing: 3,
		margin: 0,
		textAlign: "center",
		color: colors.white,
	},
	input: {
		width: 460,
		height: 24,
		fontSize: 15,
		fontFamily:
			"TrebuchetMS, Trebuchet MS, Lucida Grande, Lucida Sans Unicode, Lucida Sans, Tahoma,sans-serif",
		color: colors.black4,
		border: `1px solid ${colors.gray2}`,
		[breakpoints.mobileOnly]: {
			width: "auto",
		},
	},
	inputError: {
		border: `1px solid ${colors.errorRed}`,
	},
	label: {
		lineHeight: 1.33,
		color: colors.black4,
		fontSize: 15,
		fontFamily:
			"TrebuchetMS, Trebuchet MS, Lucida Grande, Lucida Sans Unicode, Lucida Sans, Tahoma,sans-serif",
		marginBottom: 10,
	},
	section: {
		fontFamily:
			"TrebuchetMS, Trebuchet MS, Lucida Grande, Lucida Sans Unicode, Lucida Sans, Tahoma,sans-serif",
		fontSize: 15,
		lineHeight: 1.33,
	},
	sectionHeader: {
		fontFamily: "priori-sans",
		backgroundColor: colors.lightTeal4,
		color: colors.black4,
		fontWeight: 700,
		paddingLeft: 32,
		paddingRight: 32,
		paddingTop: 12,
		paddingBottom: 8,
		fontSize: 20,
		[breakpoints.mobileOnly]: {
			fontSize: 20,
		},
	},
	sectionContent: {
		color: colors.black4,
		paddingLeft: 32,
		paddingRight: 32,
		paddingTop: 16,
		paddingBottom: 24,
	},
	linkContent: {
		display: "flex",
		[breakpoints.mobileOnly]: {
			flexDirection: "column",
		},
	},
	linkContentRight: {
		marginLeft: 20,
		display: "flex",
		flexDirection: "column",
		[breakpoints.mobileOnly]: {
			marginLeft: 0,
			marginTop: 8,
		},
	},
	linkContentRightUnlinked: {
		justifyContent: "space-between",
	},
	linkContentRightLinked: {
		justifyContent: "flex-end",
		paddingBottom: 16,
	},
	linkContentButtons: {
		display: "flex",
		alignItems: "center",
		marginTop: 12,
		[breakpoints.mobileOnly]: {
			flexDirection: "column",
			alignItems: "flex-start",
		},
	},
	linkContentButtonLeft: {
		marginRight: 16,
		height: 20,
		[breakpoints.mobileOnly]: {
			marginTop: 8,
			marginBottom: 16,
			marginRight: 0,
		},
	},
	whatsThisLinked: {
		marginTop: 8,
	},
	whatsThisUnlinked: {
		fontSize: 14,
	},
	modal: {
		position: "absolute",
		border: `1px solid ${colors.gray6}`,
		background: colors.white,
		overflow: "auto",
		borderRadius: 4,
		outline: "none",
		padding: 0,
		top: "50%",
		bottom: "auto",
		left: "50%",
		right: "auto",
		transform: "translate(-50%, -50%)",
		maxWidth: 600,
		[breakpoints.mobileOnly]: {
			maxWidth: "100vw",
			top: 0,
			bottom: 0,
			left: 0,
			right: 0,
			transform: "none",
			border: "none",
			borderRadius: 0,
		},
	},
	myWicImage: {
		height: 100,
		width: 100,
		[breakpoints.mobileOnly]: {
			height: 70,
			width: 70,
		},
	},
	myWicImageContainer: {
		position: "relative",
	},
	myWicCheckIcon: {
		height: 40,
		width: 40,
		position: "absolute",
		top: -10,
		right: -10,
	},
});

const faqLinkEnglish = "/faq";
const faqLinkSpanish = "/faq?language=es";

export default class ProfileModal extends PureComponent {
	static propTypes = {
		currentEmailAddress: PropTypes.string,
		showing: PropTypes.bool,
		language: PropTypes.string.isRequired,
		dismiss: PropTypes.func.isRequired,
		updateEmail: PropTypes.func.isRequired,
		txinLinked: PropTypes.bool,
	};

	constructor(props) {
		super(props);

		this.strings = props.language === languages.es ? spanish : english;

		this.state = {
			error: null,
			email1: "",
			email2: "",
		};
	}

	_dismiss = () => {
		this.setState({
			error: null,
			email1: "",
			email2: "",
		});
		this.props.dismiss();
	};

	_dontPropagate = e => {
		e.stopPropagation();
	};

	_setEmail1 = email => {
		this.setState({
			email1: email,
		});
	};

	_setEmail2 = email => {
		this.setState({
			email2: email,
		});
	};

	_save = e => {
		e.preventDefault();
		if (this.state.email1 !== this.state.email2) {
			this.setState({
				error: this.strings.errors.matchEmails,
			});
		} else if (this.state.email1.length < 5 || this.state.email2.length < 5) {
			this.setState({
				error: this.strings.errors.emailRequired,
			});
		} else {
			updateEmail(this.state.email1)
				.then(() => {
					this.props.updateEmail(this.state.email1);
					this._dismiss();
					NotificationManager.success(this.strings.updatedEmail);
				})
				.catch(err => {
					handleUnknownError(err);
					const errMsg =
						err.response.data.errors &&
						err.response.data.errors &&
						err.response.data.errors.email.length > 0
							? err.response.data.errors.email[0]
							: this.strings.errors.failedToUpdateEmail;
					NotificationManager.error(errMsg);
				});
		}
	};

	render() {
		if (!this.props.showing) {
			return null;
		}
		return (
			<Modal
				isOpen={this.props.showing}
				onRequestClose={this._dismiss}
				className={css(styles.modal)}
			>
				<div className={css(styles.header)}>
					<button className={css(styles.closeIconButton)} onClick={this._dismiss}>
						<img
							alt="x"
							src={`${rootUrl}/images/x.png`}
							srcSet={`${rootUrl}/images/x@2x.png 2x, ${rootUrl}/images/x@3x.png 3x`}
							width="20"
							height="20"
							className={css(styles.closeIcon)}
						/>
					</button>
					<h1 className={css(styles.headerText)}>{this.strings.PROFILE}</h1>
				</div>
				{this.props.currentEmailAddress ? (
					<div className={css(styles.section)}>
						<div className={css(styles.sectionHeader)}>
							{this.strings.CurrentEmailAddress}:
						</div>
						<div className={css(styles.sectionContent)}>
							{this.props.currentEmailAddress}
						</div>
					</div>
				) : null}
				<div className={css(styles.section)}>
					<div className={css(styles.sectionHeader)}>
						{this.strings.linkYourMyWicAccount}
					</div>
					<div className={css(styles.sectionContent, styles.linkContent)}>
						<div className={css(styles.myWicImageContainer)}>
							<img
								className={css(styles.myWicImage)}
								src={`${rootUrl}/images/my-wic.png`}
								srcSet={`${rootUrl}/images/my-wic@2x.png 2x, ${rootUrl}/images/my-wic@3x.png 3x`}
								alt={this.strings.myWic}
							/>
							{this.props.txinLinked ? (
								// TODO: use srcSet when get 3x check image
								<img
									className={css(styles.myWicCheckIcon)}
									src={`${rootUrl}/images/icon-check.png`}
									alt=""
								/>
							) : null}
						</div>
						{this.props.txinLinked ? (
							<div
								className={css(
									styles.linkContentRight,
									styles.linkContentRightLinked,
								)}
							>
								<div>{this.strings.currentlyLinkedToMyWic}</div>
								<a
									className={css(styles.whatsThisLinked)}
									href={
										this.props.language === languages.es
											? faqLinkSpanish
											: faqLinkEnglish
									}
								>
									{this.strings.whatsThis}
								</a>
							</div>
						) : (
							<div
								className={css(
									styles.linkContentRight,
									styles.linkContentRightUnlinked,
								)}
							>
								<div>{this.strings.linkToMyWicInstructions}</div>
								<div className={css(styles.linkContentButtons)}>
									<RoundButton
										className={css(styles.linkContentButtonLeft)}
										label={this.strings.linkToMyWic}
										href={`https://mywic.net/?lmsuser=${encodeURIComponent(
											this.props.currentEmailAddress,
										)}`}
									/>
									<a
										className={css(styles.whatsThisUnlinked)}
										href={
											this.props.language === languages.es
												? faqLinkSpanish
												: faqLinkEnglish
										}
									>
										{this.strings.whatsThis}
									</a>
								</div>
							</div>
						)}
					</div>
				</div>
				<div className={css(styles.section)}>
					<div className={css(styles.sectionHeader)}>
						{this.strings.ChangeEmailAddress}
					</div>
					<div className={css(styles.sectionContent)}>
						<div>
							<span>{this.strings.ChangeEmailAddressInstructions}</span>
							<a href="/faq">{this.strings.ClassesFAQ}</a>
						</div>
						<form>
							<div className={css(styles.formItem)}>
								<label htmlFor="update-email" className={css(styles.label)}>
									{this.strings.EnterNewEmail}
								</label>
								<input
									className={css([
										styles.input,
										this.state.error ? styles.inputError : "",
									])}
									id="update-email"
									type="text"
									value={this.state.email1}
									// eslint-disable-next-line react/jsx-no-bind
									onChange={e => this._setEmail1(e.target.value)}
								/>
							</div>
							<div className={css(styles.formItem)}>
								<label htmlFor="reenter-email" className={css(styles.label)}>
									{this.strings.ReenterNewEmail}
								</label>
								<input
									className={css([
										styles.input,
										this.state.error ? styles.inputError : "",
									])}
									id="reenter-email"
									type="text"
									value={this.state.email2}
									// eslint-disable-next-line react/jsx-no-bind
									onChange={e => this._setEmail2(e.target.value)}
								/>
							</div>
							<div className={css(styles.errorMessage)}>{this.state.error}</div>
							<div className={css(styles.buttonsContainer)}>
								<RoundButton
									className={css(styles.button)}
									onClick={this._save}
									label={this.strings.Save}
								/>
								<RoundButton
									className={css([styles.button, styles.cancelButton])}
									onClick={this._dismiss}
									type="unfilled"
									label={this.strings.Cancel}
								/>
							</div>
						</form>
					</div>
				</div>
			</Modal>
		);
	}
}



// WEBPACK FOOTER //
// ./src/components/ProfileModal.js